import {
  CASTING_SHRINKAGE_MATERIALS,
  MANUFACTURING_MATERIAL_OPTIONS,
  PRESS_BRAKE_MATERIALS as GLOBAL_PRESS_BRAKE_MATERIALS,
  THERMAL_EXPANSION_MATERIALS as GLOBAL_THERMAL_EXPANSION_MATERIALS,
} from "../data/machiningMaterials";

const INCH_TO_MM = 25.4;
const LB_TO_KG = 0.45359237;
const KG_TO_LB = 2.20462262;

const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const rad = (degrees) => (n(degrees) * Math.PI) / 180;
const money = (value) => `$${n(value).toFixed(2)}`;

export const MANUFACTURING_DENSITIES = MANUFACTURING_MATERIAL_OPTIONS;
export const PRESS_BRAKE_MATERIALS = GLOBAL_PRESS_BRAKE_MATERIALS;

export const WELD_PROCESSES = [
  { id: "smaw", label: "SMAW", efficiency: 0.75 },
  { id: "gmaw", label: "GMAW / FCAW", efficiency: 0.8 },
  { id: "gtaw", label: "GTAW", efficiency: 0.6 },
  { id: "saw", label: "SAW", efficiency: 0.9 },
  { id: "custom", label: "Custom", efficiency: 0.8 },
];

export const CASTING_SHRINKAGE = CASTING_SHRINKAGE_MATERIALS;
export const THERMAL_EXPANSION_MATERIALS = GLOBAL_THERMAL_EXPANSION_MATERIALS;

export const THREE_S_AREAS = [
  { id: "cell", label: "Machining cell / work center" },
  { id: "tooling", label: "Tooling crib / tool storage" },
  { id: "inspection", label: "Inspection area" },
  { id: "material", label: "Material rack / staging" },
  { id: "welding", label: "Welding / fabrication bay" },
  { id: "office", label: "Programming / office workflow" },
];

const fmtDim = (value, unit) => `${n(value).toFixed(unit === "metric" ? 3 : 4)} ${unit === "metric" ? "mm" : "in"}`;
const fmtWeight = (value, unit) => `${n(value).toFixed(3)} ${unit === "metric" ? "kg" : "lb"}`;

const getDensity = (id) =>
  MANUFACTURING_DENSITIES.find((item) => item.id === id) || MANUFACTURING_DENSITIES[0];

export const calculateBendAllowance = (input) => {
  const thickness = n(input.thickness);
  const radius = n(input.radius);
  const angle = n(input.angle);
  const kFactor = n(input.kFactor);
  const errors = [];
  if (thickness <= 0) errors.push("Material thickness must be greater than zero.");
  if (radius < 0) errors.push("Inside bend radius cannot be negative.");
  if (angle <= 0 || angle > 180) errors.push("Bend angle must be greater than 0 and no more than 180 degrees.");
  if (kFactor < 0 || kFactor > 1) errors.push("K-factor should be entered as a decimal, normally between 0 and 0.5.");
  if (errors.length) return { errors };

  // Standard sheet metal bend allowance formula.
  const bendAllowance = rad(angle) * (radius + kFactor * thickness);
  return {
    bendAllowance,
    neutralAxis: kFactor * thickness,
    formulas: ["Bend allowance = angle radians x (inside radius + K-factor x material thickness)", "Neutral axis location from inside face = K-factor x material thickness"],
    warnings: kFactor > 0.5 ? ["K-factor above 0.5 is unusual for normal press-brake bending. Verify tooling and developed blank data."] : [],
  };
};

export const calculateKFactor = (input) => {
  const bendAllowance = n(input.bendAllowance);
  const radius = n(input.radius);
  const thickness = n(input.thickness);
  const angle = n(input.angle);
  const errors = [];
  if (bendAllowance <= 0) errors.push("Bend allowance must be greater than zero.");
  if (radius < 0) errors.push("Inside bend radius cannot be negative.");
  if (thickness <= 0) errors.push("Material thickness must be greater than zero.");
  if (angle <= 0 || angle > 180) errors.push("Bend angle must be greater than 0 and no more than 180 degrees.");
  if (errors.length) return { errors };

  // Standard K-factor rearrangement from bend allowance.
  const kFactor = ((bendAllowance / rad(angle)) - radius) / thickness;
  return {
    kFactor,
    formulas: ["K = ((bend allowance / angle radians) - inside radius) / material thickness"],
    warnings: kFactor < 0 || kFactor > 0.5 ? ["Calculated K-factor is outside the common 0 to 0.5 range. Check measured bend allowance, bend angle, radius, and thickness."] : [],
  };
};

export const calculatePressBrakeTonnage = (input) => {
  const thicknessIn = input.unit === "metric" ? n(input.thickness) / INCH_TO_MM : n(input.thickness);
  const lengthIn = input.unit === "metric" ? n(input.length) / INCH_TO_MM : n(input.length);
  const vDieIn = input.unit === "metric" ? n(input.vDie) / INCH_TO_MM : n(input.vDie);
  const tensilePsi = n(input.tensilePsi);
  const errors = [];
  if (thicknessIn <= 0) errors.push("Material thickness must be greater than zero.");
  if (lengthIn <= 0) errors.push("Bend length must be greater than zero.");
  if (vDieIn <= 0) errors.push("V-die opening must be greater than zero.");
  if (tensilePsi <= 0) errors.push("Tensile strength must be greater than zero.");
  if (errors.length) return { errors };

  // Standard air-bending shop estimate, with 60 ksi mild steel as factor 1.0 basis.
  // The 575 constant is a common US tons-per-foot form, so total tonnage scales by bend length / 12.
  const tensileFactor = tensilePsi / 60000;
  const tonnagePerFoot = (575 * tensileFactor * (thicknessIn ** 2)) / vDieIn;
  const tonnage = tonnagePerFoot * (lengthIn / 12);
  return {
    tonnage,
    tonnagePerFoot,
    tonnagePerMeter: tonnage / (lengthIn * 0.0254),
    formulas: ["Tonnage per foot = (575 x tensile strength factor x thickness^2) / V-die opening", "Total tonnage = tonnage per foot x bend length in feet", "Tensile strength factor = tensile PSI / 60,000 PSI"],
    warnings: ["Estimate only. Verify press brake capacity, punch/die ratings, material certs, bend method, and tooling manufacturer charts."],
  };
};

export const calculateKerfCompensation = (input) => {
  const finalSize = n(input.finalSize);
  const kerf = n(input.kerf);
  const errors = [];
  if (finalSize <= 0) errors.push("Desired final size must be greater than zero.");
  if (kerf < 0) errors.push("Kerf width cannot be negative.");
  if (errors.length) return { errors };

  // Common CAM kerf compensation convention: offset by half the measured kerf.
  const halfKerf = kerf / 2;
  let programmedDimension = finalSize;
  let direction = "No offset is applied to the centerline path.";
  if (input.cutType === "outside") {
    programmedDimension = finalSize + kerf;
    direction = "Offset the toolpath outward by kerf / 2 so the outside profile finishes to size.";
  } else if (input.cutType === "inside") {
    programmedDimension = Math.max(0, finalSize - kerf);
    direction = "Offset the toolpath inward by kerf / 2 so the inside profile or hole finishes to size.";
  }

  return {
    adjustment: halfKerf,
    programmedDimension,
    direction,
    formulas: ["Toolpath offset = kerf width / 2", "Outside programmed dimension = final dimension + kerf", "Inside programmed dimension = final dimension - kerf"],
    warnings: ["Kerf varies with machine, consumables, material, thickness, pierce quality, and process settings."],
  };
};

export const calculateWeldHeatInput = (input) => {
  const voltage = n(input.voltage);
  const amperage = n(input.amperage);
  const travelSpeed = n(input.travelSpeed);
  const efficiency = n(input.efficiency);
  const errors = [];
  if (voltage <= 0) errors.push("Voltage must be greater than zero.");
  if (amperage <= 0) errors.push("Amperage must be greater than zero.");
  if (travelSpeed <= 0) errors.push("Travel speed must be greater than zero.");
  if (efficiency <= 0 || efficiency > 1) errors.push("Efficiency must be a decimal greater than 0 and no more than 1.");
  if (errors.length) return { errors };

  // Welding heat input estimate in kJ per entered travel-speed unit.
  const heatInput = (voltage * amperage * 60 * efficiency) / (travelSpeed * 1000);
  return {
    heatInput,
    unitLabel: input.speedUnit === "mm-min" ? "kJ/mm" : "kJ/in",
    formulas: ["Heat input = (voltage x amperage x 60 x efficiency) / (travel speed x 1000)"],
    warnings: ["Heat input is an estimate. Procedure qualification, interpass temperature, joint design, and code requirements may control accepted values."],
  };
};

export const calculateWeldCost = (input) => {
  const unit = input.unit || "inch";
  const length = n(input.length);
  const weldSize = n(input.weldSize);
  const manualFiller = n(input.fillerOverride);
  const fillerCost = n(input.fillerCost);
  const depositionEfficiency = n(input.depositionEfficiency) / 100;
  const laborRate = n(input.laborRate);
  const travelSpeed = n(input.travelSpeed);
  const setupTime = n(input.setupTime);
  const gasCost = n(input.gasCost);
  const errors = [];
  if (length <= 0) errors.push("Weld length must be greater than zero.");
  if (manualFiller <= 0 && weldSize <= 0) errors.push("Enter weld size or manual filler usage.");
  if (fillerCost < 0 || laborRate < 0 || gasCost < 0) errors.push("Cost values cannot be negative.");
  if (depositionEfficiency <= 0 || depositionEfficiency > 1) errors.push("Deposition efficiency must be greater than 0% and no more than 100%.");
  if (travelSpeed <= 0) errors.push("Travel speed must be greater than zero.");
  if (setupTime < 0) errors.push("Setup time cannot be negative.");
  if (errors.length) return { errors };

  const lengthIn = unit === "metric" ? length / INCH_TO_MM : length;
  const sizeIn = unit === "metric" ? weldSize / INCH_TO_MM : weldSize;
  const steelLbIn3 = 0.283;
  // Fillet weld estimate uses triangular cross-section area: 0.5 x leg^2.
  const theoreticalLb = manualFiller > 0
    ? (unit === "metric" ? manualFiller * KG_TO_LB : manualFiller)
    : (0.5 * sizeIn * sizeIn * lengthIn * steelLbIn3);
  const fillerUsedLb = theoreticalLb / depositionEfficiency;
  const weldMinutes = (unit === "metric" ? length : lengthIn) / travelSpeed;
  const laborHours = setupTime + weldMinutes / 60;
  const laborCost = laborHours * laborRate;
  const consumableCost = fillerUsedLb * (unit === "metric" ? fillerCost / KG_TO_LB : fillerCost) + gasCost;
  return {
    fillerUsed: unit === "metric" ? fillerUsedLb * LB_TO_KG : fillerUsedLb,
    weldMinutes,
    laborCost,
    consumableCost,
    totalCost: laborCost + consumableCost,
    formulas: ["Approx. fillet weld area = 0.5 x leg size^2", "Filler used = theoretical deposited weight / deposition efficiency", "Weld time = weld length / travel speed", "Labor cost = (setup hours + weld minutes / 60) x labor rate"],
    warnings: ["Weld cost is an estimate. Joint type, number of passes, fit-up, position, cleanup, inspection, and welder productivity can dominate actual cost."],
  };
};

const shapeVolumeIn3 = (input) => {
  const unit = input.unit || "inch";
  const scale = unit === "metric" ? 1 / INCH_TO_MM : 1;
  const length = n(input.length) * scale;
  const width = n(input.width) * scale;
  const height = n(input.height) * scale;
  const od = n(input.outerDiameter) * scale;
  const id = n(input.innerDiameter) * scale;
  const qty = Math.max(1, n(input.quantity) || 1);
  if (input.shape === "round") return Math.PI * (od / 2) ** 2 * length * qty;
  if (input.shape === "tube" || input.shape === "pipe") return Math.PI * (((od / 2) ** 2) - ((id / 2) ** 2)) * length * qty;
  if (input.shape === "square") return length * height * height * qty;
  return length * width * height * qty;
};

export const calculateMaterialWeight = (input) => {
  const density = getDensity(input.material);
  const quantity = Math.max(1, n(input.quantity) || 1);
  const totalVolume = shapeVolumeIn3(input);
  const errors = [];
  if (totalVolume <= 0) errors.push("Enter valid positive dimensions for the selected shape.");
  if ((input.shape === "tube" || input.shape === "pipe") && n(input.innerDiameter) >= n(input.outerDiameter)) {
    errors.push("Inner diameter must be smaller than outer diameter.");
  }
  if (errors.length) return { errors };

  const totalLb = totalVolume * density.lbIn3;
  const totalWeight = input.unit === "metric" ? totalLb * LB_TO_KG : totalLb;
  return {
    weightPerPart: totalWeight / quantity,
    totalWeight,
    densityLabel: density.label,
    formulas: ["Weight = volume x density", "Tube / pipe area = pi x (OD^2 - ID^2) / 4"],
    warnings: ["Material density is approximate. Use certified density or supplier weight for critical quotes."],
  };
};

export const calculatePrintCost = (input) => {
  const grams = n(input.grams);
  const spoolCost = n(input.spoolCost);
  const spoolGrams = n(input.spoolGrams);
  const printHours = n(input.printHours);
  const electricityRate = n(input.electricityRate);
  const printerWatts = n(input.printerWatts);
  const setupHours = n(input.setupHours);
  const laborRate = n(input.laborRate);
  const failureRate = n(input.failureRate) / 100;
  const markup = n(input.markup) / 100;
  const errors = [];
  if (grams <= 0 || spoolCost < 0 || spoolGrams <= 0) errors.push("Enter valid filament use, spool cost, and spool weight.");
  if (printHours < 0 || printerWatts < 0 || electricityRate < 0) errors.push("Print time and electricity values cannot be negative.");
  if (setupHours < 0 || laborRate < 0) errors.push("Labor values cannot be negative.");
  if (failureRate < 0 || failureRate >= 1) errors.push("Failure rate must be at least 0% and less than 100%.");
  if (errors.length) return { errors };

  // Additive quote math based on slicer material use, print time, failure rate, and markup.
  const materialCost = grams * spoolCost / spoolGrams;
  const electricityCost = (printerWatts / 1000) * printHours * electricityRate;
  const laborCost = setupHours * laborRate;
  const baseCost = materialCost + electricityCost + laborCost;
  const failureAdjustedCost = baseCost / (1 - failureRate);
  const suggestedPrice = failureAdjustedCost * (1 + markup);
  return {
    materialCost,
    electricityCost,
    laborCost,
    baseCost,
    failureAdjustedCost,
    suggestedPrice,
    formulas: ["Material cost = grams used x spool cost / spool grams", "Electricity cost = printer kW x print hours x electricity rate", "Failure adjusted cost = base cost / (1 - failure rate)", "Suggested price = failure adjusted cost x (1 + markup)"],
    warnings: ["Use slicer-reported material/time when possible. Failure rate, post-processing, machine depreciation, and packing time can change real cost."],
  };
};

export const calculateCastingShrinkage = (input) => {
  const finalDimension = n(input.finalDimension);
  const shrinkRate = n(input.shrinkRate);
  const errors = [];
  if (finalDimension <= 0) errors.push("Final dimension must be greater than zero.");
  if (shrinkRate < 0) errors.push("Shrinkage rate cannot be negative.");
  if (errors.length) return { errors };

  // Common foundry pattern allowance estimate using percent linear shrinkage.
  const patternDimension = finalDimension * (1 + shrinkRate / 100);
  return {
    patternDimension,
    shrinkAllowance: patternDimension - finalDimension,
    formulas: ["Pattern dimension = final dimension x (1 + shrinkage rate)"],
    warnings: ["Approximate only. Actual shrinkage depends on alloy, mold, geometry, section thickness, feeding, and foundry practice."],
  };
};

export const calculateJobQuote = (input) => {
  const materialCost = n(input.materialCost);
  const purchasedPartsCost = n(input.purchasedPartsCost);
  const programmingTime = n(input.programmingTime);
  const setupTime = n(input.setupTime);
  const cycleTime = n(input.cycleTime);
  const quantity = Math.max(1, Math.round(n(input.quantity) || 1));
  const machineRate = n(input.machineRate);
  const laborRate = n(input.laborRate);
  const toolingCost = n(input.toolingCost);
  const outsideCost = n(input.outsideCost);
  const scrapRate = n(input.scrapRate) / 100;
  const overheadRate = n(input.overheadRate) / 100;
  const profitRate = n(input.profitRate) / 100;
  const errors = [];
  if ([materialCost, purchasedPartsCost, programmingTime, setupTime, cycleTime, machineRate, laborRate, toolingCost, outsideCost].some((value) => value < 0)) {
    errors.push("Cost, time, and rate values cannot be negative.");
  }
  if (quantity <= 0) errors.push("Quantity must be greater than zero.");
  if (scrapRate < 0 || scrapRate >= 1) errors.push("Scrap/rework percentage must be at least 0% and less than 100%.");
  if (profitRate < 0 || profitRate >= 1) errors.push("Profit margin percentage must be at least 0% and less than 100%.");
  if (errors.length) return { errors };

  // General job-shop estimate: direct cost, scrap adjustment, overhead, then margin.
  const directMaterial = materialCost + purchasedPartsCost;
  const laborHours = programmingTime + setupTime + (cycleTime * quantity) / 60;
  const machineHours = setupTime + (cycleTime * quantity) / 60;
  const laborCost = laborHours * laborRate;
  const machineCost = machineHours * machineRate;
  const directCost = directMaterial + laborCost + machineCost + toolingCost + outsideCost;
  const scrapAdjustedCost = directCost / (1 - scrapRate);
  const overheadCost = scrapAdjustedCost * overheadRate;
  const breakEven = scrapAdjustedCost + overheadCost;
  const suggestedQuote = breakEven / (1 - profitRate);
  const profitAmount = suggestedQuote - breakEven;
  return {
    totalEstimatedCost: breakEven,
    costPerPart: breakEven / quantity,
    suggestedQuote,
    suggestedPricePerPart: suggestedQuote / quantity,
    profitAmount,
    breakEven,
    formulas: ["Labor hours = programming + setup + cycle minutes x quantity / 60", "Machine hours = setup + cycle minutes x quantity / 60", "Direct cost = material + purchased parts + labor + machine + tooling + outside processing", "Scrap adjusted cost = direct cost / (1 - scrap rate)", "Break-even = scrap adjusted cost + overhead", "Suggested quote = break-even / (1 - profit margin)"],
    warnings: ["Estimating tool only. Confirm customer requirements, inspection, payment terms, delivery risk, and shop capacity before quoting."],
  };
};

export const calculateToleranceStack = (input) => {
  const unit = input.unit || "inch";
  const features = [1, 2, 3, 4]
    .map((index) => ({
      nominal: n(input[`nominal${index}`]),
      plus: n(input[`plus${index}`]),
      minus: n(input[`minus${index}`]),
      active:
        input[`nominal${index}`] !== "" ||
        input[`plus${index}`] !== "" ||
        input[`minus${index}`] !== "",
    }))
    .filter((feature) => feature.active);
  const errors = [];
  if (!features.length) errors.push("Enter at least one contributing dimension.");
  if (features.some((feature) => feature.plus < 0 || feature.minus < 0)) {
    errors.push("Tolerance values should be entered as positive values.");
  }
  if (errors.length) return { errors };

  // Standard tolerance stack methods: arithmetic worst-case and RSS estimate.
  const nominalStack = features.reduce((sum, feature) => sum + feature.nominal, 0);
  const worstHigh = features.reduce((sum, feature) => sum + feature.nominal + feature.plus, 0);
  const worstLow = features.reduce((sum, feature) => sum + feature.nominal - feature.minus, 0);
  const rssPlus = Math.sqrt(features.reduce((sum, feature) => sum + feature.plus ** 2, 0));
  const rssMinus = Math.sqrt(features.reduce((sum, feature) => sum + feature.minus ** 2, 0));
  return {
    nominalStack,
    contributorCount: features.length,
    worstLow,
    worstHigh,
    worstBand: worstHigh - worstLow,
    rssLow: nominalStack - rssMinus,
    rssHigh: nominalStack + rssPlus,
    rssBand: rssPlus + rssMinus,
    formulas: [
      "Worst-case maximum = sum(nominal + plus tolerance)",
      "Worst-case minimum = sum(nominal - minus tolerance)",
      "RSS estimate = sqrt(sum(each tolerance^2))",
    ],
    warnings: [
      `Results are in ${unit === "metric" ? "millimeters" : "inches"}. RSS assumes independent contributors and should be reviewed against the inspection/control plan.`,
    ],
  };
};

export const calculateOEE = (input) => {
  const plannedMinutes = n(input.plannedMinutes);
  const downtimeMinutes = n(input.downtimeMinutes);
  const idealCycleSeconds = n(input.idealCycleSeconds);
  const totalCount = n(input.totalCount);
  const goodCount = n(input.goodCount);
  const errors = [];
  if (plannedMinutes <= 0) errors.push("Planned production time must be greater than zero.");
  if (downtimeMinutes < 0 || downtimeMinutes >= plannedMinutes) errors.push("Downtime must be at least zero and less than planned time.");
  if (idealCycleSeconds <= 0) errors.push("Ideal cycle time must be greater than zero.");
  if (totalCount <= 0) errors.push("Total count must be greater than zero.");
  if (goodCount < 0 || goodCount > totalCount) errors.push("Good count must be between zero and total count.");
  if (errors.length) return { errors };

  // Standard OEE calculation: Availability x Performance x Quality.
  const runMinutes = plannedMinutes - downtimeMinutes;
  const availability = runMinutes / plannedMinutes;
  const performance = (idealCycleSeconds * totalCount) / (runMinutes * 60);
  const quality = goodCount / totalCount;
  const oee = availability * performance * quality;
  const scrapCount = totalCount - goodCount;
  const idealOutputAtRunTime = (runMinutes * 60) / idealCycleSeconds;
  return {
    runMinutes,
    availability,
    performance,
    quality,
    oee,
    scrapCount,
    idealOutputAtRunTime,
    formulas: [
      "Availability = run time / planned production time",
      "Performance = ideal cycle time x total count / run time",
      "Quality = good count / total count",
      "OEE = availability x performance x quality",
    ],
    warnings: ["OEE depends on accurate downtime, ideal cycle, scrap, and count tracking. Keep definitions consistent by work center."],
  };
};

export const calculateTaktCapacity = (input) => {
  const availableMinutes = n(input.availableMinutes);
  const demand = n(input.demand);
  const cycleSeconds = n(input.cycleSeconds);
  const efficiency = n(input.efficiency) / 100;
  const errors = [];
  if (availableMinutes <= 0) errors.push("Available production time must be greater than zero.");
  if (demand <= 0) errors.push("Required demand must be greater than zero.");
  if (cycleSeconds <= 0) errors.push("Cycle time must be greater than zero.");
  if (efficiency <= 0 || efficiency > 1) errors.push("Efficiency must be greater than 0% and no more than 100%.");
  if (errors.length) return { errors };

  // Common production planning math for takt and capacity.
  const taktSeconds = (availableMinutes * 60) / demand;
  const effectiveCycleSeconds = cycleSeconds / efficiency;
  const capacity = (availableMinutes * 60) / effectiveCycleSeconds;
  const requiredResources = effectiveCycleSeconds / taktSeconds;
  const capacityGap = capacity - demand;
  return {
    taktSeconds,
    effectiveCycleSeconds,
    capacity,
    capacityGap,
    requiredResources,
    formulas: [
      "Takt time = available production seconds / customer demand",
      "Effective cycle time = observed cycle time / efficiency",
      "Capacity = available production seconds / effective cycle time",
      "Required resources = effective cycle time / takt time",
    ],
    warnings: ["Capacity planning is an estimate. Include changeovers, breaks, maintenance, inspection, material handling, and constraint operations."],
  };
};

export const calculateThermalExpansion = (input) => {
  const unit = input.unit || "inch";
  const originalLength = n(input.originalLength);
  const startTemp = n(input.startTemp);
  const endTemp = n(input.endTemp);
  const ppmC = n(input.ppmC);
  const errors = [];
  if (originalLength <= 0) errors.push("Original length must be greater than zero.");
  if (ppmC <= 0) errors.push("Coefficient of thermal expansion must be greater than zero.");
  if (errors.length) return { errors };

  const deltaC = input.tempUnit === "f"
    ? (endTemp - startTemp) * 5 / 9
    : endTemp - startTemp;
  const coefficient = ppmC / 1000000;
  // Linear thermal expansion formula.
  const lengthChange = originalLength * coefficient * deltaC;
  return {
    deltaTempC: deltaC,
    deltaTempF: deltaC * 9 / 5,
    lengthChange,
    finalLength: originalLength + lengthChange,
    formulas: ["Length change = original length x coefficient of thermal expansion x temperature change"],
    warnings: [`Coefficient is approximate unless entered from material data. Result is in ${unit === "metric" ? "mm" : "in"}. Geometry and restraint can change actual movement.`],
  };
};

export const calculateSheetYield = (input) => {
  const sheetLength = n(input.sheetLength);
  const sheetWidth = n(input.sheetWidth);
  const partLength = n(input.partLength);
  const partWidth = n(input.partWidth);
  const edgeMargin = n(input.edgeMargin);
  const kerf = n(input.kerf);
  const requiredQuantity = n(input.requiredQuantity);
  const errors = [];
  if ([sheetLength, sheetWidth, partLength, partWidth].some((value) => value <= 0)) {
    errors.push("Sheet and part dimensions must be greater than zero.");
  }
  if (edgeMargin < 0 || kerf < 0) errors.push("Edge margin and kerf cannot be negative.");
  if (errors.length) return { errors };

  const usableLength = Math.max(0, sheetLength - edgeMargin * 2);
  const usableWidth = Math.max(0, sheetWidth - edgeMargin * 2);
  const countFor = (length, width) => {
    const x = Math.floor((usableLength + kerf) / (length + kerf));
    const y = Math.floor((usableWidth + kerf) / (width + kerf));
    return Math.max(0, x) * Math.max(0, y);
  };
  const straightCount = countFor(partLength, partWidth);
  const rotatedCount = countFor(partWidth, partLength);
  const partCount = Math.max(straightCount, rotatedCount);
  const sheetArea = sheetLength * sheetWidth;
  const usedArea = partCount * partLength * partWidth;
  return {
    usableLength,
    usableWidth,
    straightCount,
    rotatedCount,
    partCount,
    utilization: sheetArea > 0 ? usedArea / sheetArea : 0,
    scrapPercent: sheetArea > 0 ? Math.max(0, 1 - usedArea / sheetArea) : 0,
    sheetsRequired: requiredQuantity > 0 && partCount > 0 ? Math.ceil(requiredQuantity / partCount) : 0,
    formulas: [
      "Usable sheet size = sheet size - 2 x edge margin",
      "Parts per direction = floor((usable dimension + kerf) / (part dimension + kerf))",
      "Utilization = total part area / sheet area",
    ],
    warnings: ["Rectangular layout estimate only. True nesting depends on part shape, lead-ins, tabs, grain direction, clamps, remnant strategy, and CAM nesting."],
  };
};

export const calculateHydraulicForce = (input) => {
  const pressure = n(input.pressure);
  const diameter = n(input.diameter);
  const efficiency = n(input.efficiency) / 100;
  const errors = [];
  if (pressure <= 0) errors.push("Pressure must be greater than zero.");
  if (diameter <= 0) errors.push("Cylinder diameter must be greater than zero.");
  if (efficiency <= 0 || efficiency > 1) errors.push("Efficiency must be greater than 0% and no more than 100%.");
  if (errors.length) return { errors };

  const area = Math.PI * (diameter / 2) ** 2;
  const force = pressure * area * efficiency;
  const kiloNewtons = force * 0.0044482216;
  return {
    area,
    force,
    kiloNewtons,
    tons: force / 2000,
    formulas: ["Cylinder area = pi x diameter^2 / 4", "Force = pressure x area x efficiency", "US tons = force in lbf / 2,000"],
    warnings: ["Use rated working pressure and cylinder data. Real press force depends on hydraulic losses, seals, frame deflection, tooling, and safety factor."],
  };
};

export const calculateInventoryReorder = (input) => {
  const averageDailyUse = n(input.averageDailyUse);
  const leadTimeDays = n(input.leadTimeDays);
  const safetyStock = n(input.safetyStock);
  const orderQuantity = n(input.orderQuantity);
  const errors = [];
  if (averageDailyUse < 0 || leadTimeDays < 0 || safetyStock < 0 || orderQuantity < 0) {
    errors.push("Inventory values cannot be negative.");
  }
  if (averageDailyUse === 0 && safetyStock === 0) errors.push("Enter daily use or safety stock.");
  if (errors.length) return { errors };

  const leadTimeDemand = averageDailyUse * leadTimeDays;
  const reorderPoint = leadTimeDemand + safetyStock;
  const daysCovered = averageDailyUse > 0 ? (reorderPoint + orderQuantity) / averageDailyUse : 0;
  return {
    leadTimeDemand,
    reorderPoint,
    maxStockAfterOrder: reorderPoint + orderQuantity,
    daysCovered,
    formulas: ["Lead-time demand = average daily use x lead time", "Reorder point = lead-time demand + safety stock", "Max after order = reorder point + order quantity"],
    warnings: ["Reorder point is an estimating/control tool. Supplier reliability, minimum buys, shelf life, price breaks, and demand variation should be reviewed."],
  };
};

export const calculateProcessCapability = (input) => {
  const lsl = n(input.lsl);
  const usl = n(input.usl);
  const mean = n(input.mean);
  const sigma = n(input.sigma);
  const errors = [];
  if (usl <= lsl) errors.push("USL must be greater than LSL.");
  if (sigma <= 0) errors.push("Standard deviation must be greater than zero.");
  if (errors.length) return { errors };

  // Common process capability indices for normally distributed process data.
  const cp = (usl - lsl) / (6 * sigma);
  const cpk = Math.min((usl - mean) / (3 * sigma), (mean - lsl) / (3 * sigma));
  const centeredOffset = mean - ((usl + lsl) / 2);
  const nearestSpecDistance = Math.min(usl - mean, mean - lsl);
  return {
    cp,
    cpk,
    centeredOffset,
    nearestSpecDistance,
    toleranceWidth: usl - lsl,
    formulas: ["Cp = (USL - LSL) / (6 x sigma)", "Cpk = min((USL - mean) / (3 x sigma), (mean - LSL) / (3 x sigma))", "Center offset = mean - midpoint of tolerance"],
    warnings: ["Cp/Cpk assumes stable process behavior and meaningful sampling. Confirm normality, gage capability, subgrouping, and control chart stability before relying on capability indices."],
  };
};

export const calculateThreeSAudit = (input) => {
  const scores = [
    n(input.sortNeededRemoved),
    n(input.sortRedTags),
    n(input.sortObsoleteControls),
    n(input.setLocations),
    n(input.setVisualControls),
    n(input.setPointOfUse),
    n(input.shineCleanliness),
    n(input.shineInspection),
    n(input.shineOwnerStandard),
  ];
  const errors = [];
  if (scores.some((score) => score < 0 || score > 5)) errors.push("Each 3S score must be between 0 and 5.");
  if (errors.length) return { errors };

  const sortScore = scores.slice(0, 3).reduce((sum, score) => sum + score, 0) / 15;
  const setScore = scores.slice(3, 6).reduce((sum, score) => sum + score, 0) / 15;
  const shineScore = scores.slice(6, 9).reduce((sum, score) => sum + score, 0) / 15;
  const overall = (sortScore + setScore + shineScore) / 3;
  const overallPercent = overall * 100;
  const actionLevel =
    overallPercent >= 85
      ? "Controlled 3S condition"
      : overallPercent >= 65
        ? "Stable but needs countermeasures"
        : overallPercent >= 45
          ? "Unstable workplace condition"
          : "Immediate 3S recovery required";

  const weakest = [
    ["Sort", sortScore],
    ["Set in Order", setScore],
    ["Shine", shineScore],
  ].sort((a, b) => a[1] - b[1])[0][0];

  return {
    sortScore: sortScore * 100,
    setScore: setScore * 100,
    shineScore: shineScore * 100,
    overallScore: overallPercent,
    actionLevel,
    weakest,
    formulas: [
      "Sort score = Sort points / 15 x 100",
      "Set in Order score = Set points / 15 x 100",
      "Shine score = Shine points / 15 x 100",
      "Overall 3S score = average of Sort, Set in Order, and Shine",
    ],
    warnings: [
      "3S is a lean workplace organization audit, not a substitute for safety, quality, maintenance, or process capability controls.",
      "Use consistent scoring definitions and audit the same area repeatedly to make trends meaningful.",
    ],
  };
};

export const calculateRedTagRecovery = (input) => {
  const people = Math.max(1, n(input.people) || 1);
  const minutesPerShift = n(input.minutesLostPerPerson);
  const shiftsPerMonth = n(input.shiftsPerMonth);
  const laborRate = n(input.laborRate);
  const redTagItems = Math.max(0, n(input.redTagItems));
  const minutesToDisposition = n(input.minutesToDisposition);
  const errors = [];
  if (minutesPerShift < 0 || shiftsPerMonth < 0 || laborRate < 0 || minutesToDisposition < 0) errors.push("Time and cost values cannot be negative.");
  if (errors.length) return { errors };

  const monthlyLostHours = (people * minutesPerShift * shiftsPerMonth) / 60;
  const monthlyLostCost = monthlyLostHours * laborRate;
  const redTagRecoveryHours = (redTagItems * minutesToDisposition) / 60;
  const firstMonthNetHours = Math.max(0, monthlyLostHours - redTagRecoveryHours);
  const firstMonthNetSavings = firstMonthNetHours * laborRate;

  return {
    monthlyLostHours,
    monthlyLostCost,
    redTagRecoveryHours,
    firstMonthNetHours,
    firstMonthNetSavings,
    formulas: [
      "Monthly lost hours = people x minutes lost per shift x shifts per month / 60",
      "Monthly lost cost = monthly lost hours x labor rate",
      "Red-tag disposition hours = red-tag items x minutes per item / 60",
      "First-month net recovered hours = monthly lost hours - red-tag disposition hours",
    ],
    warnings: [
      "This is an estimate for visible waste from searching, walking, waiting, and clutter. It does not include safety incidents, quality escapes, or machine downtime.",
      "Red-tag items should have a disposition owner and date so clutter is removed instead of relocated.",
    ],
  };
};

export const formatManufacturingResult = {
  dim: fmtDim,
  weight: fmtWeight,
  money,
  percent: (value, digits = 1) => `${(n(value) * 100).toFixed(digits)}%`,
  number: (value, digits = 3) => n(value).toFixed(digits),
};
